import React, { ReactNode, useCallback, useMemo, useState } from 'react';
import { AsyncPaginate } from 'react-select-async-paginate';
import { handleAPI } from '../../../utils/api/api';
import Label from '../Label/Label';
import { deserializeData } from 'utils/helper/helper';
import AuthWarningTooltip from '../AuthWarningTooltip/AuthWarningTooltip';
import useHasAbility from '../../../hooks/auth/useHasAbility';
import { TooltipData } from '../tooltip/tooltip';
import type { MenuPlacement } from 'react-select';
import { getter } from 'utils/helper/accessors';
import { useStaticOptions } from './useStaticOptions';
import { useValue } from './useValue';
import { CustomOption } from './CustomOption/CustomOption';
import { CustomMultiValue } from './CustomMultiValue/CustomMultiValue';
import { CustomIndicatorsContainer } from './CustomIndicatorsContainer/CustomIndicatorsContainer';

const findClosestModal = (ref) => {
  return ref?.closest('.modal') || null;
};

const determinePortalTarget = (ref, showOnBody, showOnModal) => {
  if (showOnBody) {
    return document.getElementById('body');
  } else if (showOnModal) {
    return findClosestModal(ref);
  }
  return null;
};

export interface MultiSelectProps {
  endPoint?: string;
  selectedOptions?: any;
  setSelectedOptions?: (args: any) => void;
  isMulti?: boolean;
  valueField?: string;
  options?: Record<string, { id: any; name: string }> | any[];
  placeholder?: string;
  infoData?: TooltipData;
  label?: ReactNode;
  include?: string;
  displayValue?: string;
  isDisabled?: boolean;
  isRequired?: boolean;
  isSearchable?: boolean;
  sortOptions?: boolean;
  isClearable?: boolean;
  labelClassName?: string;
  className?: string;
  queryParams?: any;
  menuPlacement?: MenuPlacement;
  showOnBody?: boolean;
  showOnModal?: boolean;
  searchBy?: string;
  filterRecordById?: any;
  error?: string;
  controllerField?: any;
  mapAsyncDataFunc?: (data: any) => any;
  defaultElement?: string;
  onBlur?: (args: any) => void;
  onChange?: (args: any) => void;
  defaultElementOnlyWhenData?: boolean;
  abilitiesKey?: string;
  isReprocessing?: boolean;
  enabledOnly?: boolean;
  forceWideMenu?: boolean;
  testId?: string;
  prependOptions?: any[];
  controlShouldRenderValue?: boolean;
}

const defaultGetter = (option) => option?.id;

const MultiSelect = ({
  endPoint,
  selectedOptions,
  setSelectedOptions,
  isMulti,
  options,
  valueField,
  placeholder = 'Select from the list',
  infoData,
  label,
  include,
  displayValue = 'name',
  isDisabled = false,
  isRequired = false,
  isSearchable = true,
  sortOptions = true,
  isClearable = true,
  labelClassName,
  className,
  queryParams,
  menuPlacement = 'auto',
  showOnBody = false,
  showOnModal = true,
  searchBy = 'filter[name_cont]',
  filterRecordById,
  error,
  controllerField,
  mapAsyncDataFunc,
  defaultElement,
  onBlur,
  onChange,
  defaultElementOnlyWhenData = false,
  abilitiesKey,
  isReprocessing = false,
  enabledOnly = true,
  forceWideMenu = false,
  testId,
  prependOptions,
  controlShouldRenderValue = true,
}: MultiSelectProps) => {
  const hasAbility = useHasAbility(abilitiesKey);
  const [componentRef, setComponentRef] = useState(undefined);
  const [inputValue, setInputValue] = useState('');
  const [authError, setAuthError] = useState(!hasAbility);

  const handleInputChange = (newValue, actionMeta) => {
    if (actionMeta.action === 'input-change') {
      setInputValue(newValue);
    }
  };

  const loadOptions = async (searchQuery, _, { page }) => {
    const searchParam = searchBy;

    const requestParams = {
      page: page,
      per_page: 20,
      ...(searchQuery && { [searchParam]: searchQuery }),
      ...(enabledOnly ? { 'filter[enabled_eq]': true } : {}),
      'filter[s]': `${displayValue} asc`,
      include: include,
      ...queryParams,
    };

    const response = await handleAPI(endPoint, 'get', requestParams);
    if (response?.status === 403) {
      setAuthError(true);
      return { options: [] };
    }

    if (response.status !== 200 && response.status !== 201) {
      return { options: [] };
    }
    let data = [];

    const isPoolGatewayEndpoint = endPoint && endPoint.includes('/pools/') && endPoint.includes('/gateways');

    if (isPoolGatewayEndpoint) {
      data = response.data.included
        .filter((item) => item.type === 'gateway')
        .map((gateway) => ({
          id: gateway.id,
          title: gateway.attributes.title || gateway.attributes.name || `Gateway #${gateway.id}`,
          name: gateway.attributes.name || gateway.attributes.title || `Gateway #${gateway.id}`,
          mid: gateway.attributes.mid || null,
          [displayValue]:
            gateway.attributes[displayValue] ||
            gateway.attributes.title ||
            gateway.attributes.name ||
            `Gateway #${gateway.id}`,
        }));
    } else {
      data = deserializeData(response.data) as any[];
    }

    if (mapAsyncDataFunc) {
      data = mapAsyncDataFunc(data);
    }

    if (filterRecordById) {
      data = data.filter((item) => item?.id !== filterRecordById);
    }

    if (isReprocessing && searchQuery) {
      data = data.filter((item) => {
        const searchFields = [item.title, item.name, item.mid];
        return searchFields.some((field) => field && field.toLowerCase().includes(searchQuery.toLowerCase()));
      });
    }
    let returnData;
    if (isReprocessing) {
      returnData = data.map((item) => ({
        ...item,
        title: item.title || item.name || item.mid || `Gateway #${item.id}`,
        name: item.name || item.title || item.mid || `Gateway #${item.id}`,
        [displayValue]: item[displayValue] || item.title || item.name || item.mid || `Gateway #${item.id}`,
      }));
    } else {
      returnData = include ? data[include] : data ?? response.data;
    }

    const shouldIncludeDefault =
      defaultElement && (!defaultElementOnlyWhenData || (returnData && returnData.length > 0));

    const finalOptions = shouldIncludeDefault ?
      [...[{ [displayValue]: defaultElement, id: 0 }], ...returnData] :
      returnData;

    const finalOptionsWithPrepend = prependOptions?.length ? [...prependOptions, ...finalOptions] : finalOptions;

    return {
      options: finalOptionsWithPrepend,
      hasMore: response.data.meta ? response.data.meta.total_count / 20 > page : false,
      additional: { page: page + 1 },
    };
  };

  const valueGetter = useMemo(() => valueField ? (option) => getter(option, valueField) : defaultGetter, [valueField]);
  const textGetter = useCallback((option) => getter(option, displayValue), [displayValue]);
  const [staticOptions, loadStaticOptions] = useStaticOptions({ options, textGetter, sortOptions, endPoint });
  const { value, handleOnChange } = useValue({ valueField, staticOptions, isMulti, controllerField, selectedOptions, onChange, valueGetter, endPoint, setSelectedOptions });

  const handleOnBlur = (e) => {
    controllerField?.onBlur(e);
    onBlur?.(e);
  };

  const menuPortalTarget = determinePortalTarget(componentRef, showOnBody, showOnModal);

  return (
    <div
      data-testid={testId || controllerField?.name || 'multiselect'}
      ref={setComponentRef}
      className={`relative ${authError && label && 'bg-gray-2'} ${isDisabled || authError ? 'cursor-not-allowed opacity-50' : ''}`}
    >
      {authError && label && (
        <div className={`absolute right-0 top-[2px] z-50`}>
          <AuthWarningTooltip />
        </div>
      )}
      {label && (
        <Label
          label={label}
          infoData={infoData}
          required={isRequired}
          className={labelClassName}
        />
      )}
      <AsyncPaginate
        loadOptions={endPoint ? loadOptions : loadStaticOptions}
        isMulti={isMulti}
        getOptionValue={valueGetter}
        getOptionLabel={textGetter}
        value={value}
        onChange={handleOnChange}
        onBlur={handleOnBlur}
        selectRef={controllerField?.ref}
        additional={{ page: 1 }}
        isSearchable={isSearchable}
        closeMenuOnSelect={!isMulti}
        blurInputOnSelect={false}
        placeholder={placeholder}
        debounceTimeout={endPoint ? 500 : 0}
        controlShouldRenderValue={controlShouldRenderValue}
        components={{
          Option: CustomOption,
          MultiValue: CustomMultiValue,
          IndicatorsContainer: authError && !label ? AuthWarningTooltip : CustomIndicatorsContainer,
        }}
        className={`!pointer-events-auto text-sm ${className}`}
        classNames={{
          control: (state) => `
            px-3 py-[0.5625rem] !min-h-auto !rounded-[5px] relative !bg-transparent
            ${state.isFocused && '!border-primary'}
            ${error ? '!border-error' : '!border-gray-5'}
            ${(authError && !label) || isDisabled ? '!text-gray-5 !cursor-not-allowed' : ''}`,
          valueContainer: () => '!p-0',
          input: () => '!p-0 !m-0 !ml-1 [&>*]:!text-text',
          placeholder: () => '!text-gray-5 !m-0 !text-sm pr-2',
          singleValue: () => '!text-text !p-0 !m-0 !text-sm',
          menu: () =>
            `!shadow-none !bg-transparent !z-[10000] !my-0 ${showOnModal ? '!max-h-[150px]' : ''} ${forceWideMenu ? '!w-auto !min-w-[200px]' : ''}`,
          menuList: () =>
            `!p-0 !rounded-[0.375rem] !z-50 !shadow-select-menu !bg-transparent ${showOnModal ? '!max-h-[150px]' : ''} ${forceWideMenu ? '!w-auto !min-w-[200px]' : ''}`,
          noOptionsMessage: () => 'bg-gray-4',
          loadingMessage: () => 'bg-gray-4',
          menuPortal: () => '!z-[1000]',
        }}
        defaultMenuIsOpen={false}
        isDisabled={isDisabled || authError}
        hideSelectedOptions={false}
        cacheUniqs={[endPoint, options]}
        menuPlacement={menuPlacement}
        menuPortalTarget={menuPortalTarget}
        isClearable={!isRequired && isClearable}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onMenuClose={() => setInputValue('')}
      />
      <p className='text-xs text-error'>{error}</p>
    </div>
  );
};

export default MultiSelect;
